import { Truck } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useShipmentInventoryPolicy } from "@/hooks/useShipmentInventoryPolicy";

type ShippingMethodChoice = "self_delivery" | "logistics";

interface ShippingTransitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shippingMethodChoice: ShippingMethodChoice;
  onShippingMethodChange: (method: ShippingMethodChoice) => void;
  carrierName: string;
  onCarrierNameChange: (value: string) => void;
  trackingNumber: string;
  onTrackingNumberChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

export default function ShippingTransitionDialog({
  open,
  onOpenChange,
  shippingMethodChoice,
  onShippingMethodChange,
  carrierName,
  onCarrierNameChange,
  trackingNumber,
  onTrackingNumberChange,
  onCancel,
  onConfirm,
  isPending,
}: ShippingTransitionDialogProps) {
  const { query: inventoryPolicy } = useShipmentInventoryPolicy(open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="shippingDialog"
        className="border border-line bg-surface text-ink sm:max-w-[425px]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-ink">
            <Truck className="h-5 w-5 text-progress" />
            安排出貨方式
          </DialogTitle>
          <DialogDescription className="text-sm text-ink-muted">
            確認配送方式後，系統會同步更新狀態並開啟該群組的出貨單列印頁。
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-4">
          <div data-testid="shippingInventoryPolicy" className="min-w-0 rounded-md border border-line bg-raised/60 p-3 text-sm text-ink-muted [overflow-wrap:anywhere]">
            {inventoryPolicy.isError ? <>
              <p>無法讀取扣料模式；確認出貨時仍由伺服器套用目前設定。</p>
              <Button type="button" variant="outline" className="mt-2" disabled={inventoryPolicy.isFetching} onClick={() => void inventoryPolicy.refetch()}>重新讀取模式</Button>
            </> : inventoryPolicy.isPending || inventoryPolicy.isFetching || !inventoryPolicy.data ? <p>正在讀取出貨扣料模式…</p> : <p>{inventoryPolicy.data.enabled
              ? "BOM 扣料已啟用：出貨須有 BOM，並自動扣除原料。"
              : "BOM 扣料已暫停：本次出貨不要求 BOM，也不更新原料庫存；成本分析仍可能依現有配方估算，不代表本次實際成本。"}</p>}
            <p className="mt-1 text-xs">模式可能由管理員切換，實際結果以伺服器出貨當下的設定與出貨原始紀錄為準。</p>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-ink-muted">配送方式</label>
            <div className="flex gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-ink">
                <input
                  id="shippingMethodSelfDelivery"
                  type="radio"
                  value="self_delivery"
                  checked={shippingMethodChoice === "self_delivery"}
                  onChange={() => onShippingMethodChange("self_delivery")}
                  className="accent-action"
                />
                自送
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-ink">
                <input
                  id="shippingMethodLogistics"
                  type="radio"
                  value="logistics"
                  checked={shippingMethodChoice === "logistics"}
                  onChange={() => onShippingMethodChange("logistics")}
                  className="accent-action"
                />
                物流配送
              </label>
            </div>
          </div>

          {shippingMethodChoice === "logistics" && (
            <>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-muted">物流公司</label>
                <Input
                  id="shippingCarrierNameInput"
                  value={carrierName}
                  onChange={(event) => onCarrierNameChange(event.target.value)}
                  placeholder="例如：黑貓、新竹物流"
                  className="glass-input h-10"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-muted">追蹤編號</label>
                <Input
                  id="shippingTrackingNumberInput"
                  value={trackingNumber}
                  onChange={(event) => onTrackingNumberChange(event.target.value)}
                  placeholder="輸入託運或追蹤編號"
                  className="glass-input h-10"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-line pt-4">
          <Button
            variant="outline"
            data-testid="shippingCancelBtn"
            onClick={onCancel}
            className="border-line-strong bg-transparent text-ink hover:bg-raised"
          >
            取消
          </Button>
          <Button
            data-testid="shippingConfirmBtn"
            onClick={onConfirm}
            disabled={isPending}
            className="bg-action text-white hover:bg-action-hover"
          >
            {isPending ? "處理中..." : "確認出貨"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
